/*
  Smartein theme.js

  Global behaviour, ported from the inline script in
  smartein-mirror/v2/de/index.html. Vanilla, no framework, no build step,
  same as the source.

  What is ported verbatim:
    - mobile nav toggle, including the aria-expanded handling and the
      close-on-link-click
    - the .scrolled state on the fixed nav past 40px
    - reveal on scroll with the staggered transition delay
    - the muted video play and pause observer

  What is deliberately NOT ported:
    - Lenis smooth scroll. It came from a CDN, fought Shopify's own scroll
      restoration and made the cart drawer need data-lenis-prevent. If the
      client wants the momentum feel back, add it as a section setting.
    - the localStorage cart, the hardcoded product array, cartPermalink and
      the Storefront API cartCreate call. The native Shopify cart replaces
      all of it. See smartein-theme-docs/build-notes/structure-map.md section 1.7.
    - the fake newsletter handler that only rewrote the button label.

  One necessary change: the mirror selected reveal targets with
  "main > section". Shopify wraps every section in a div, so that direct
  child selector matches nothing here. The selector below walks one level
  deeper and is otherwise identical.
*/
(function () {
  "use strict";

  var navEl = null;
  var navToggle = null;

  var closeMenu = function () {
    if (!navEl) return;
    navEl.classList.remove("menu-open");
    if (navToggle) navToggle.setAttribute("aria-expanded", "false");
  };

  var onScroll = function () {
    if (navEl) navEl.classList.toggle("scrolled", window.scrollY > 40);
  };

  // The header is a section now, so the theme editor can replace the whole nav
  // node while the page is open. The element handles are therefore looked up
  // inside this function rather than captured once at boot, and bindNav runs
  // again on shopify:section:load. The listeners below go on the fresh nodes;
  // the discarded ones take their listeners with them, so nothing doubles up.
  var bindNav = function () {
    navEl = document.getElementById("nav");
    navToggle = document.getElementById("navToggle");
    onScroll();
    if (!navEl || !navToggle) return;
    navToggle.addEventListener("click", function () {
      var open = navEl.classList.toggle("menu-open");
      navToggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
    navEl.querySelectorAll(".navlinks a").forEach(function (a) {
      a.addEventListener("click", closeMenu);
    });
  };

  bindNav();

  // document level, so it is registered once and survives a header reload
  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") closeMenu();
  });
  window.addEventListener("scroll", onScroll, { passive: true });

  var revealItems = document.querySelectorAll(
    "#MainContent > section:not(.hero), #MainContent > .shopify-section > section:not(.hero), .product, .proof-item, .mini-reel"
  );
  revealItems.forEach(function (item) {
    item.classList.add("reveal");
  });

  // subtle stagger: siblings in the same group ease in one after another
  var groups = new Map();
  revealItems.forEach(function (item) {
    if (item.matches(".product, .proof-item, .mini-reel")) {
      var arr = groups.get(item.parentElement) || [];
      arr.push(item);
      groups.set(item.parentElement, arr);
    }
  });
  groups.forEach(function (arr) {
    arr.forEach(function (item, i) {
      item.style.transitionDelay = Math.min(i * 70, 350) + "ms";
    });
  });

  /* Videos are deliberately NOT force-loaded. preload="none" plus the
     IntersectionObserver below means a video only downloads once it scrolls
     into view, which keeps the homepage payload small. The collection itself
     is still needed by the observer wiring further down. */
  var videos = document.querySelectorAll("video");

  if ("IntersectionObserver" in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -8% 0px" }
    );
    revealItems.forEach(function (item) {
      observer.observe(item);
    });

    var videoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && video.muted) {
            video.play().catch(function () {});
          } else if (video.muted) {
            video.pause();
          }
        });
      },
      { threshold: 0.28 }
    );
    videos.forEach(function (video) {
      videoObserver.observe(video);
    });
  } else {
    revealItems.forEach(function (item) {
      item.classList.add("is-visible");
    });
  }

  window.setTimeout(function () {
    revealItems.forEach(function (item) {
      item.classList.add("is-visible");
    });
  }, 1200);

  // Theme editor: re-run the reveal pass when a section is re-rendered so
  // freshly injected markup is not stuck at opacity 0, and rebind the nav when
  // the section that carries it is the one that came back.
  document.addEventListener("shopify:section:load", function (event) {
    event.target.querySelectorAll(".reveal").forEach(function (item) {
      item.classList.add("is-visible");
    });
    if (event.target.querySelector("#nav")) bindNav();
    bindCompareScroll();
  });

  // Compare table: the row is intentionally wider than a phone viewport, so
  // hides the right-edge fade once the shopper has scrolled to the end,
  // which is the only signal that there was ever more to see.
  function bindCompareScroll() {
    document.querySelectorAll("[data-compare-scroll]").forEach(function (wrap) {
      if (wrap.dataset.scrollBound) return;
      wrap.dataset.scrollBound = "1";
      var scroller = wrap.querySelector(".ctable");
      if (!scroller) return;
      function update() {
        var atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
        wrap.classList.toggle("at-end", atEnd);
      }
      scroller.addEventListener("scroll", update, { passive: true });
      update();
    });
  }
  bindCompareScroll();
})();
